import { type Article, ArticleType } from '@/domain/article.types'
import { useBrandArticles } from '@/hooks/useArticles'
import { apiErrorMessage } from '@/lib/api-error'
import { brandArticleComposerLink } from '@/lib/article-composer-link'
import { BrandDetailsPanel } from './BrandDetailsPanel'

/**
 * The Brand page's Brand details tab: the brand's `brand_details` article.
 *
 * Resolved on the BRAND AXIS ALONE, with no narrowing by the market code the page
 * carries. The page holds a market code but no applicability ladder for it, so it
 * cannot tell an article classified to the region above the market (which applies)
 * from one on a sibling market (which does not), and narrowing on the code alone
 * would silently drop the former. The campaigns card on this page already follows
 * that rule, and the two must not disagree about what "this brand" means.
 *
 * The type filter is what keeps a `general` article out: placement is decided by
 * the surface asking for the type it wants, not by the API withholding rows.
 */

/**
 * The one article the tab shows.
 *
 * Most recently updated wins, with no uniqueness rule on the data - a brand may
 * hold several. The read already asks the API for that order; re-establishing it
 * here is what makes the rule the TAB's rather than a query parameter's, so it
 * cannot be lost to a change in how the list is fetched.
 */
export function mostRecentArticle(articles: readonly Article[]): Article | null {
	return articles.reduce<Article | null>(
		(latest, article) =>
			latest === null || Date.parse(article.updatedAt) > Date.parse(latest.updatedAt) ? article : latest,
		null,
	)
}

export function BrandDetailsTab({ brandCode, brandName }: { brandCode: string; brandName: string }) {
	const {
		data: articles = [],
		isLoading,
		isError,
		error,
		refetch,
	} = useBrandArticles(brandCode, ArticleType.BRAND_DETAILS)

	return (
		<BrandDetailsPanel
			article={mostRecentArticle(articles)}
			brandName={brandName}
			isLoading={isLoading}
			error={isError ? apiErrorMessage(error, 'The brand details could not be read.') : null}
			onRetry={() => void refetch()}
			createTo={brandArticleComposerLink(brandCode)}
		/>
	)
}
